import React, { useEffect, useState } from 'react';
import { useNotifications } from '../contexts/NotificationsContext';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  Chip,
  Container,
  MenuItem,
  useMediaQuery,
  Skeleton,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Person,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  AccountBalance,
  TrendingUp,
  Close,
  EmojiEvents,
  Lightbulb,
  Refresh,
  CalendarToday,
  DateRange,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { dedupeCollectionCases } from '../utils/loandiskId';
import { useAuth } from '../contexts/AuthContext';
import { PostgrestResponse } from '@supabase/supabase-js';
import { format, subDays, subMonths, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth } from 'date-fns';
import { getContextualTip, CollectionTip } from '../data/collectionTips';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { styled } from '@mui/material/styles';
import NotificationsPanel from '../components/NotificationsPanel';
import { fetchAllPaginated } from '../utils/supabasePagination';
import StoriesBar from '../components/stories/StoriesBar';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Format currency helper
const formatCurrency = (value: number | string | null): string => {
  if (!value) return '0.00';
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

interface BorrowerData {
  name: string;
  mobile_no: string;
  email: string;
  address: string;
}

interface CollectionCase {
  id: string;
  account_balance: number;
  status: string;
  loandisk_id: string;
  agent_id: string | null;
  borrower: {
    name: string;
    mobile_no: string;
    email: string;
    address: string;
  };
}

// Add this interface for the raw data from Supabase
interface RawCollectionData {
  id: string;
  account_balance: number;
  status: string;
  loandisk_id: string | null;
  agent_id: string | null;
  borrower: {
    name: string;
    mobile_no: string;
    email: string;
    address: string;
  } | {
    name: string;
    mobile_no: string;
    email: string;
    address: string;
  }[];
}

// Data coming directly from Supabase. Note: Depending on your relationship,
// the joined borrower data might come as an array or an object.
interface ForCollectionData {
  id: string;
  borrower_id: string;
  account_balance: number;
  status: string;
  agent_name: string | null;
  loandisk_id: string | null;
  borrower: BorrowerData[] | BorrowerData;
}

interface AssignmentFormData {
  loandisk_id: string;
  agent_name: string;
}

interface CollectorSummary {
  collector_id: string;
  collector_name: string;
  total_collections: number;
}

interface TeamCollectorSummary {
  collector_id: string;
  collector_name: string;
  avatar_url?: string | null;
  total_collections: number;
  this_week: number;
  last_week: number;
  this_month: number;
  last_month: number;
  week_change_pct: number;
  month_change_pct: number;
}

interface MonthlyCollectionData {
  month: string;
  amount: number;
}

// Add these helper components at the top
const StatCard = ({ title, value, icon, trend, trendValue, color }: any) => {
  const theme = useTheme();
  
  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}25`,
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ 
            p: 0.75,
            borderRadius: 1.5,
            bgcolor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
          <IconButton size="small" sx={{ p: 0.5 }}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
        
        <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 'bold' }}>
          {value}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: trend === 'up' ? theme.palette.success.main : theme.palette.error.main 
          }}>
            {trend === 'up' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
            <Typography variant="caption" sx={{ ml: 0.5 }}>
              {trendValue}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Add this styled component for table headers
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
  whiteSpace: 'nowrap'
}));

// Add this interface for agents
interface Agent {
  id: string;
  username: string;
  role: string;
}

// First, add this type for assignment notifications
interface AssignmentNotification extends Notification {
  reference_id: string;
  borrower_name?: string;
}

const AgentDashboard: React.FC = () => {
  const { 
    notifications, 
    markAsRead, 
    setNotifications, 
    markAllAsRead,
    getCollectionIdFromNotification 
  } = useNotifications();
  const { user, userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const borrowerId = searchParams.get('borrower');
  const [unassignedCases, setUnassignedCases] = useState<CollectionCase[]>([]);
  const [collectorSummaries, setCollectorSummaries] = useState<CollectorSummary[]>([]);
  const [totalMonthlyCollection, setTotalMonthlyCollection] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState<CollectionCase | null>(null);
  const [formData, setFormData] = useState<AssignmentFormData>({
    loandisk_id: '',
    agent_name: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [monthlyCollections, setMonthlyCollections] = useState<MonthlyCollectionData[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState<boolean>(false);

  // Insights & rankings state
  const [todayCollection, setTodayCollection] = useState<number>(0);
  const [lastWeekSameDayCollection, setLastWeekSameDayCollection] = useState<number>(0);
  const [lastMonthSameDayCollection, setLastMonthSameDayCollection] = useState<number>(0);
  const [weeklyCollection, setWeeklyCollection] = useState<number>(0);
  const [lastWeekCollection, setLastWeekCollection] = useState<number>(0);
  const [dailyRank, setDailyRank] = useState<{ rank: number; total: number } | null>(null);
  const [weeklyRank, setWeeklyRank] = useState<{ rank: number; total: number } | null>(null);
  const [monthlyRank, setMonthlyRank] = useState<{ rank: number; total: number } | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [currentTip, setCurrentTip] = useState<CollectionTip>(getContextualTip());

  // Team Momentum + Leaderboard state
  const [teamSummaries, setTeamSummaries] = useState<TeamCollectorSummary[]>([]);
  const [teamInsights, setTeamInsights] = useState<{
    teamThisWeek: number; teamLastWeek: number;
    teamThisMonth: number; teamLastMonth: number;
    teamWeekChangePct: number; teamMonthChangePct: number;
    topPerformer: string; needsAttention: string[];
  }>({
    teamThisWeek: 0, teamLastWeek: 0, teamThisMonth: 0, teamLastMonth: 0,
    teamWeekChangePct: 0, teamMonthChangePct: 0, topPerformer: '', needsAttention: [],
  });
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [todayByAgent, setTodayByAgent] = useState<Record<string, number>>({});
  const [teamLoading, setTeamLoading] = useState(false);

  useEffect(() => {
    console.log('Fetching unassigned cases...');
    fetchUnassignedCases();
    fetchCollectionSummaries();
    fetchMonthlyCollections();
    fetchAgents();
    fetchNotifications();
    fetchInsightsAndRankings();

    // Get the current user ID for subscriptions
    const getCurrentUserId = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        return data?.user?.id;
      } catch (error) {
        console.error('Error getting user ID for subscription:', error);
        return null;
      }
    };

    // Set up real-time subscription for notifications
    const setupSubscription = async () => {
      const userId = await getCurrentUserId();
      if (!userId) {
        console.error('Cannot set up subscription - no user ID');
        return;
      }

      console.log('Setting up notification subscription for user:', userId);
      
      const subscription = supabase
        .channel('notifications-changes-agent')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `recipient_id=eq.${userId}`
          },
          (payload) => {
            console.log('Real-time notification update:', payload);
            
            // Handle different payload events
            if (payload.eventType === 'INSERT') {
              const newNotification = payload.new as any;
              
              // Only add relevant notification types
              if (
                newNotification.notification_type === 'assignment' ||
                newNotification.notification_type === 'collection_case' ||
                newNotification.notification_type === 'comment_mention' || 
                newNotification.notification_type === 'update_mention' ||
                newNotification.notification_type === 'reminder'
              ) {
                console.log('Adding new notification:', newNotification);
                setNotifications(prev => [newNotification, ...prev]);
                
                // Show browser notification for mentions and reminders if supported
                if ((newNotification.notification_type === 'comment_mention' || 
                     newNotification.notification_type === 'update_mention' ||
                     newNotification.notification_type === 'reminder') &&
                    window.Notification && 
                    Notification.permission === 'granted') {
                  new Notification('Samofund', {
                    body: newNotification.message,
                    icon: '/samofund-logo.png'
                  });
                }
              }
            } 
            else if (payload.eventType === 'UPDATE') {
              // Update the notification in the list
              console.log('Updating notification:', payload.new);
              setNotifications(prev => 
                prev.map(notif => 
                  notif.id === payload.new.id ? payload.new as any : notif
                )
              );
            } 
            else if (payload.eventType === 'DELETE') {
              // Remove the notification from the list
              console.log('Removing notification:', payload.old);
              setNotifications(prev => 
                prev.filter(notif => notif.id !== payload.old.id)
              );
            }
          }
        )
        .subscribe((status) => {
          console.log('Subscription status:', status);
        });

      return subscription;
    };

    // Set up the subscription
    let subscription: any;
    setupSubscription().then(sub => {
      subscription = sub;
    });

    return () => {
      if (subscription) {
        console.log('Unsubscribing from notifications channel');
        subscription.unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    console.log('Loading:', loading);
    console.log('Unassigned cases:', unassignedCases);
  }, [loading, unassignedCases]);

  // Re-run all user-dependent fetches once auth has resolved (user?.id may be null on first render)
  useEffect(() => {
    if (!user?.id || !userProfile) return;
    fetchInsightsAndRankings();
    fetchCollectionSummaries();
    fetchMonthlyCollections();
    fetchUnassignedCases();
    fetchTeamData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userProfile?.id]);

  const fetchUnassignedCases = async () => {
    try {
      setLoading(true);

      if (!user?.id) return;

      const { data, error } = await supabase
        .from('for_collection')
        .select(`
          id,
          account_balance,
          status,
          loandisk_id,
          agent_id,
          borrower:borrowers!inner(
            name,
            mobile_no,
            email,
            address
          )
        `)
        .eq('agent_id', user.id) // Only fetch cases assigned to logged-in user
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        const transformedData: CollectionCase[] = (data as RawCollectionData[]).map(item => ({
          id: item.id,
          account_balance: Number(item.account_balance),
          status: item.status,
          loandisk_id: item.loandisk_id || '',
          agent_id: item.agent_id,
          borrower: Array.isArray(item.borrower) 
            ? {
                name: item.borrower[0].name,
                mobile_no: item.borrower[0].mobile_no,
                email: item.borrower[0].email,
                address: item.borrower[0].address
              }
            : item.borrower
        }));

        setUnassignedCases(dedupeCollectionCases(transformedData));
      }
    } catch (error) {
      console.error('Error fetching assigned cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionSummaries = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      if (!user?.id) return;

      // Match by collector_id (primary) OR collector_name (fallback for receipts
      // entered by someone else on behalf of this agent).
      const orFilter = userProfile?.username
        ? `collector_id.eq.${user.id},collector_name.eq.${userProfile.username}`
        : `collector_id.eq.${user.id}`;

      const rows = await fetchAllPaginated<{ total_loan_deductions: number | null }>(
        async (from, to) =>
          await supabase
            .from('receipts_unified')
            .select('total_loan_deductions')
            .or(orFilter)
            .neq('branch_id', 'e459fac1-dc7f-4a14-a248-f4c63843c42c')
            .gte('date', firstDay.toISOString())
            .lte('date', lastDay.toISOString())
            .order('date', { ascending: true })
            .range(from, to)
      );

      const total = rows.reduce(
        (sum, receipt) => sum + (Number(receipt.total_loan_deductions) || 0),
        0
      );

      // Set only the logged-in user's summary
      setCollectorSummaries([{
        collector_id: user.id,
        collector_name: userProfile?.username || 'Unknown',
        total_collections: total
      }]);
      
      setTotalMonthlyCollection(total);

    } catch (error) {
      console.error('Error fetching collection summaries:', error);
    }
  };

  const fetchMonthlyCollections = async () => {
    try {
      setLoadingMonthly(true);
      const now = new Date();
      const startOfYear = new Date(now.getFullYear(), 0, 1);

      if (!user?.id) {
        setLoadingMonthly(false);
        return;
      }

      const orFilter = userProfile?.username
        ? `collector_id.eq.${user.id},collector_name.eq.${userProfile.username}`
        : `collector_id.eq.${user.id}`;

      const data = await fetchAllPaginated<{
        date: string;
        total_loan_deductions: number | null;
      }>(
        async (from, to) =>
          await supabase
            .from('receipts_unified')
            .select('date, total_loan_deductions')
            .or(orFilter)
            .neq('branch_id', 'e459fac1-dc7f-4a14-a248-f4c63843c42c')
            .gte('date', startOfYear.toISOString())
            .lte('date', now.toISOString())
            .order('date', { ascending: true })
            .range(from, to)
      );

      // Initialize all months of the year with zero values
      const monthlyData: { [key: string]: number } = {};
      for (let i = 0; i < 12; i++) {
        const monthKey = format(new Date(now.getFullYear(), i, 1), 'MMM');
        monthlyData[monthKey] = 0;
      }

      // Process the data by month
      data.forEach(receipt => {
        const date = new Date(receipt.date);
        const monthKey = format(date, 'MMM');
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + Number(receipt.total_loan_deductions || 0);
      });

      // Convert to array format for chart
      const chartData = Object.entries(monthlyData).map(([month, amount]) => ({
        month,
        amount
      }));

      setMonthlyCollections(chartData);
    } catch (error) {
      console.error('Error fetching monthly collections:', error);
    } finally {
      setLoadingMonthly(false);
    }
  };

  const fetchInsightsAndRankings = async () => {
    if (!user?.id) return;
    setInsightsLoading(true);

    const EXCLUDED_BRANCH = 'e459fac1-dc7f-4a14-a248-f4c63843c42c';
    const now = new Date();

    const makeDayRange = (d: Date) => ({
      start: new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString(),
      end: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString(),
    });

    const todayRange = makeDayRange(now);
    const lwsdRange = makeDayRange(subDays(now, 7));
    const lmsdRange = makeDayRange(subMonths(now, 1));

    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekStartStr = weekStart.toISOString();
    const weekEndStr = now.toISOString();

    const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
    const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
    const lastWeekStartStr = lastWeekStart.toISOString();
    const lastWeekEndStr = lastWeekEnd.toISOString();

    const monthStartStr = startOfMonth(now).toISOString();

    // Build collector_name OR filter for this agent's own receipts
    const myName = userProfile?.username || '';
    const myOrFilter = myName
      ? `collector_id.eq.${user.id},collector_name.eq.${myName}`
      : `collector_id.eq.${user.id}`;

    // Fetch all agent profiles for ranking (role='agent' only)
    const { data: agentProfiles } = await supabase
      .from('profiles')
      .select('id, username')
      .eq('role', 'agent');

    const agentById: Record<string, string> = {};
    const agentByName: Record<string, string> = {};
    (agentProfiles || []).forEach((a: any) => {
      agentById[a.id] = a.id;
      agentByName[a.username.toLowerCase()] = a.id;
    });

    // Helper: parse amount from borrower_updates content
    const parseAmount = (content: string | null): number => {
      if (!content) return 0;
      const m = content.match(/Payment posted:\s*([\d.]+)/);
      return m ? Number(m[1]) || 0 : 0;
    };

    // Helper: resolve borrower_update entry to an agent UUID (agent role only)
    const resolveAgentFromUpdate = (u: { agent_id: string | null; agent_name?: string | null }): string | null => {
      if (u.agent_name) {
        const id = agentByName[u.agent_name.toLowerCase()];
        if (id) return id;
      }
      if (u.agent_id && agentById[u.agent_id]) return u.agent_id;
      return null;
    };

    // Helper: resolve agent from a borrower_update entry to MY agent UUID
    const isMyUpdate = (u: { agent_id: string | null; agent_name?: string | null }): boolean => {
      if (u.agent_name && myName && u.agent_name.toLowerCase() === myName.toLowerCase()) return true;
      if (u.agent_id === user.id) return true;
      return false;
    };

    try {
      const sumUpdates = (data: any[] | null) =>
        (data || []).filter(isMyUpdate).reduce((sum, u) => sum + parseAmount(u.content), 0);

      // My today — use borrower_updates for consistent data access
      const { data: todayData } = await supabase
        .from('borrower_updates')
        .select('content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', todayRange.start)
        .lte('created_at', todayRange.end)
        .is('deleted_at', null)
        .limit(500);
      const todayTotal = sumUpdates(todayData);
      setTodayCollection(todayTotal);

      // Last week same day
      const { data: lwsdData } = await supabase
        .from('borrower_updates')
        .select('content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', lwsdRange.start)
        .lte('created_at', lwsdRange.end)
        .is('deleted_at', null)
        .limit(500);
      setLastWeekSameDayCollection(sumUpdates(lwsdData));

      // Last month same day
      const { data: lmsdData } = await supabase
        .from('borrower_updates')
        .select('content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', lmsdRange.start)
        .lte('created_at', lmsdRange.end)
        .is('deleted_at', null)
        .limit(500);
      setLastMonthSameDayCollection(sumUpdates(lmsdData));

      // My this week
      const { data: weekData } = await supabase
        .from('borrower_updates')
        .select('content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', weekStartStr)
        .lte('created_at', weekEndStr)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(2000);
      setWeeklyCollection(sumUpdates(weekData));

      // My last week
      const { data: lastWeekData } = await supabase
        .from('borrower_updates')
        .select('content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', lastWeekStartStr)
        .lte('created_at', lastWeekEndStr)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(2000);
      setLastWeekCollection(sumUpdates(lastWeekData));

      // Rankings helper — uses borrower_updates, agent role only
      const computeRank = (
        data: any[] | null,
        myAgentId: string,
        setter: React.Dispatch<React.SetStateAction<{ rank: number; total: number } | null>>
      ) => {
        if (!data) return;
        const totals: Record<string, number> = {};
        data.forEach(u => {
          const agentId = resolveAgentFromUpdate(u);
          if (agentId) {
            totals[agentId] = (totals[agentId] || 0) + parseAmount(u.content);
          }
        });
        const sorted = Object.entries(totals).sort((a, b) => b[1] - a[1]);
        const idx = sorted.findIndex(([id]) => id === myAgentId);
        setter({ rank: idx === -1 ? sorted.length + 1 : idx + 1, total: sorted.length });
      };

      // Daily ranking (all agents)
      const { data: allTodayData } = await supabase
        .from('borrower_updates')
        .select('content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', todayRange.start)
        .lte('created_at', todayRange.end)
        .is('deleted_at', null)
        .limit(1000);
      computeRank(allTodayData, user.id, setDailyRank);

      // Weekly ranking (all agents)
      const { data: allWeekData } = await supabase
        .from('borrower_updates')
        .select('content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', weekStartStr)
        .lte('created_at', weekEndStr)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(2000);
      computeRank(allWeekData, user.id, setWeeklyRank);

      // Monthly ranking (all agents)
      const { data: allMonthData } = await supabase
        .from('borrower_updates')
        .select('content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', monthStartStr)
        .lte('created_at', now.toISOString())
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(5000);
      computeRank(allMonthData, user.id, setMonthlyRank);

    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  const fetchTeamData = async () => {
    setTeamLoading(true);
    try {
      const now = new Date();
      const thisMonthStart = startOfMonth(now);
      const thisMonthEnd = endOfMonth(now);
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      const lastMonthEnd = endOfMonth(subMonths(now, 1));
      const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 });
      const thisWeekEnd = endOfWeek(now, { weekStartsOn: 1 });
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const dayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();

      const { data: collectorsData, error: collectorsError } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .eq('role', 'agent');
      if (collectorsError || !collectorsData || collectorsData.length === 0) return;

      const agentById: Record<string, { id: string; username: string; avatar_url?: string | null }> = {};
      const agentByName: Record<string, { id: string; username: string; avatar_url?: string | null }> = {};
      collectorsData.forEach((a: any) => { agentById[a.id] = a; agentByName[a.username.toLowerCase()] = a; });

      const { data: updateData } = await supabase
        .from('borrower_updates')
        .select('created_at, content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', lastMonthStart.toISOString())
        .lte('created_at', thisMonthEnd.toISOString())
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(5000);

      const agentPayments: Record<string, { date: string; amount: number }[]> = {};
      collectorsData.forEach((a: any) => { agentPayments[a.id] = []; });

      (updateData || []).forEach((u: any) => {
        const m = u.content?.match(/Payment posted:\s*([\d.]+)/);
        if (!m) return;
        const amount = Number(m[1]) || 0;
        if (amount <= 0) return;
        let agentId: string | null = null;
        if (u.agent_name && agentByName[u.agent_name.toLowerCase()]) agentId = agentByName[u.agent_name.toLowerCase()].id;
        else if (u.agent_id && agentById[u.agent_id]) agentId = u.agent_id;
        if (agentId && agentPayments[agentId]) agentPayments[agentId].push({ date: u.created_at, amount });
      });

      const summaries: TeamCollectorSummary[] = collectorsData.map((collector: any) => {
        let thisMonth = 0, lastMonth = 0, thisWeek = 0, lastWeek = 0;
        agentPayments[collector.id]?.forEach(r => {
          const d = new Date(r.date);
          if (d >= thisMonthStart && d <= thisMonthEnd) thisMonth += r.amount;
          if (d >= lastMonthStart && d <= lastMonthEnd) lastMonth += r.amount;
          if (d >= thisWeekStart && d <= thisWeekEnd) thisWeek += r.amount;
          if (d >= lastWeekStart && d <= lastWeekEnd) lastWeek += r.amount;
        });
        const weekChangePct = lastWeek > 0 ? ((thisWeek - lastWeek) / lastWeek) * 100 : (thisWeek > 0 ? 100 : 0);
        const monthChangePct = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : (thisMonth > 0 ? 100 : 0);
        return { collector_id: collector.id, collector_name: collector.username, avatar_url: collector.avatar_url ?? null,
          total_collections: thisMonth, this_week: thisWeek, last_week: lastWeek, this_month: thisMonth,
          last_month: lastMonth, week_change_pct: weekChangePct, month_change_pct: monthChangePct };
      });

      const sorted = summaries.sort((a, b) => b.total_collections - a.total_collections);
      setTeamSummaries(sorted);

      const teamThisWeek = sorted.reduce((s, c) => s + c.this_week, 0);
      const teamLastWeek = sorted.reduce((s, c) => s + c.last_week, 0);
      const teamThisMonth = sorted.reduce((s, c) => s + c.this_month, 0);
      const teamLastMonth = sorted.reduce((s, c) => s + c.last_month, 0);
      const teamWeekChangePct = teamLastWeek > 0 ? ((teamThisWeek - teamLastWeek) / teamLastWeek) * 100 : (teamThisWeek > 0 ? 100 : 0);
      const teamMonthChangePct = teamLastMonth > 0 ? ((teamThisMonth - teamLastMonth) / teamLastMonth) * 100 : (teamThisMonth > 0 ? 100 : 0);
      setTeamInsights({
        teamThisWeek, teamLastWeek, teamThisMonth, teamLastMonth,
        teamWeekChangePct, teamMonthChangePct,
        topPerformer: sorted.length > 0 ? sorted[0].collector_name : '',
        needsAttention: sorted.filter(c => c.week_change_pct < -20 || (c.this_week === 0 && c.last_week > 0)).map(c => c.collector_name),
      });

      // Today per agent
      const { data: todayData } = await supabase
        .from('borrower_updates')
        .select('content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', dayStart)
        .lte('created_at', dayEnd)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1000);

      const byAgent: Record<string, number> = {};
      (todayData || []).forEach((u: any) => {
        const m = u.content?.match(/Payment posted:\s*([\d.]+)/);
        if (!m) return;
        const amount = Number(m[1]) || 0;
        if (amount <= 0) return;
        let agentId: string | null = null;
        if (u.agent_name && agentByName[u.agent_name.toLowerCase()]) agentId = agentByName[u.agent_name.toLowerCase()].id;
        else if (u.agent_id && agentById[u.agent_id]) agentId = u.agent_id;
        if (agentId) byAgent[agentId] = (byAgent[agentId] || 0) + amount;
      });
      setTodayByAgent(byAgent);
    } catch (err) {
      console.error('Error fetching team data:', err);
    } finally {
      setTeamLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, role')
        .in('role', ['agent', 'superagent'])
        .order('username');

      if (error) throw error;
      setAgents(data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handleAssign = async () => {
    if (!selectedCase || !formData.agent_name) {
      setError('Please select an agent');
      return;
    }

    try {
      // First get the agent's profile ID
      const { data: assignedAgent, error: agentError } = await supabase
        .from('profiles')
        .select('id, username')
        .eq('username', formData.agent_name)
        .single();

      if (agentError || !assignedAgent) throw new Error('Agent not found');

      console.log('Selected agent:', assignedAgent);

      // Update for_collection table with agent_id
      const { data: updateData, error: updateError } = await supabase
        .from('for_collection')
        .update({ 
          agent_id: assignedAgent.id,  // Use agent_id instead of agent_name
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedCase.id)
        .select();

      console.log('Update result:', updateData);

      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }

      if (!updateData || updateData.length === 0) {
        throw new Error('Failed to update record');
      }

      // Calculate expiry date (30 days from now)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30);

      // Create notification for the assigned agent
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          recipient_id: assignedAgent.id,
          message: `You have been assigned to ${selectedCase.borrower?.name} (${selectedCase.status}). Coordinate with Collection Supervisor for more info.`,
          category: 'collection',
          notification_type: 'assignment',
          is_read: false,
          is_active: true,
          reference_id: selectedCase.id,
          expiry_date: expiryDate.toISOString().split('T')[0],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (notificationError) throw notificationError;

      // Update local state
      setUnassignedCases(prev => 
        prev.filter(c => c.id !== selectedCase.id)
      );
      setSelectedCase(null);
      setFormData({ agent_name: '', loandisk_id: '' });
      setError(null);

    } catch (error) {
      console.error('Error assigning case:', error);
      setError('Failed to assign case. Please try again.');
    }
  };

  // Helper function for handling notification clicks including mentions
  const handleNotificationWithMentions = async (notificationId: string) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification) return;
    
    try {
      // Mark as read
      await markAsRead(notificationId);

      // Determine the correct domain - use the current origin
      const targetDomain = window.location.origin;
        
      console.log(`Using target domain: ${targetDomain}`);

      // Handle navigation based on notification type
      if (notification.notification_type === 'assignment' && notification.reference_id) {
        // Navigate to collection case details
        const url = `${targetDomain}/borrower/${notification.reference_id}`;
        console.log(`Navigating to: ${url}`);
        window.location.href = url;
      } 
      else if (notification.notification_type === 'reminder') {
        // For reminders, get the collection ID
        const collectionId = await getCollectionIdFromNotification(notification);
        
        if (collectionId) {
          // Navigate to the borrower profile
          const url = `${targetDomain}/borrower/${collectionId}`;
          console.log(`Navigating to reminder for collection ID: ${collectionId}`);
          window.location.href = url;
        } else {
          console.error('Could not find collection ID for reminder notification:', notification);
          alert('Unable to navigate to the reminder. Please try accessing it through the borrower profiles page instead.');
        }
      }
      else if (notification.notification_type === 'comment_mention' || 
               notification.notification_type === 'update_mention') {
        // For mentions, we need to get the collection ID
        const collectionId = await getCollectionIdFromNotification(notification);
        if (collectionId) {
          // Navigate to the borrower profile
          const url = `${targetDomain}/borrower/${collectionId}`;
          console.log(`Navigating to: ${url}`);
          window.location.href = url;
        } else {
          console.error('Could not find collection ID for mention notification:', notification);
          alert('Unable to navigate to the referenced content. Please try again later or contact support if the issue persists.');
        }
      }
      else if (notification.notification_type === 'collection_case' || notification.notification_type === 'itinerary_submitted') {
        // Itinerary submission/approval notification — navigate to Approved Itinerary page.
        // 'collection_case' kept for old rows; 'itinerary_submitted' is the aligned type.
        const url = `${targetDomain}/approved-itinerary`;
        console.log(`Navigating to approved itinerary for itinerary notification`);
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error processing notification:', error);
      alert('Failed to process notification. Please try again later.');
    }
  };

  // Update the handleDismissNotification function to return Promise
  const handleDismissNotification = async (notificationId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', notificationId);

      if (error) throw error;
      
      // Update local state to remove the notification
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error dismissing notification:', error);
      throw error; // Rethrow to allow notification card to handle it
    }
  };

  // Add a clearAll function to clear all notifications
  const handleClearAllNotifications = async (): Promise<void> => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ 
          is_active: false,
          is_read: true,
          updated_at: new Date().toISOString(),
          deleted_at: new Date().toISOString() // Mark all as permanently deleted
        })
        .eq('recipient_id', user?.id);

      if (error) throw error;
      
      // Update local state
      setNotifications([]);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  };

  // Enhance the fetchNotifications function
  const fetchNotifications = async (): Promise<void> => {
    setLoadingNotifications(true);
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !currentUser) {
        console.error('Error getting current user:', userError);
        return;
      }
      
      // Simplified query - only check is_active flag
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', currentUser.id)
        .eq('is_active', true)  // Only get active notifications
        .or('notification_type.eq.assignment,notification_type.eq.collection_case,notification_type.eq.itinerary_submitted,notification_type.eq.comment_mention,notification_type.eq.update_mention,notification_type.eq.reminder')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
        return;
      }
      
      // Filter out expired notifications
      if (data) {
        const now = new Date();
        const activeNotifications = data.filter(notification => {
          const expiryDate = new Date(notification.expiry_date);
          return expiryDate > now;
        });
        
        // Update is_active to false for expired notifications
        const expiredNotifications = data.filter(notification => {
          const expiryDate = new Date(notification.expiry_date);
          return expiryDate <= now;
        });
        
        if (expiredNotifications.length > 0) {
          const expiredIds = expiredNotifications.map(n => n.id);
          await supabase
            .from('notifications')
            .update({ 
              is_active: false,
              deleted_at: new Date().toISOString() // Permanently mark as deleted
            })
            .in('id', expiredIds);
        }
        
        setNotifications(activeNotifications);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // First, add a function to filter cases by type
  const filterCasesByType = (cases: CollectionCase[], type: 'collection' | 'ci') => {
    return cases.filter(caseItem => 
      type === 'ci' ? caseItem.status === 'For CI' : caseItem.status === 'For Collection'
    );
  };

  // Update the button and status chip styles
  const StatusChip = ({ status }: { status: string }) => (
    <Chip 
      label={status}
      size="small"
      sx={{
        bgcolor: status === 'For Collection' ? 'success.lighter' : 'warning.lighter',
        color: status === 'For Collection' ? 'success.dark' : 'warning.dark',
        fontWeight: 'medium',
        fontSize: '0.75rem',
        height: '24px',
        '& .MuiChip-label': {
          px: 1.5
        }
      }}
    />
  );

  const AssignButton = ({ onClick }: { onClick: () => void }) => (
    <Button 
      variant="contained"
      size="small"
      onClick={onClick}
      sx={{
        textTransform: 'none',
        boxShadow: 'none',
        borderRadius: 1,
        px: 2,
        py: 0.5,
        fontSize: '0.75rem',
        fontWeight: 'medium',
        bgcolor: 'success.main',
        color: 'white',
        '&:hover': {
          boxShadow: 'none',
          bgcolor: 'success.dark',
        }
      }}
    >
      Assign
    </Button>
  );

  // Add a separate useEffect to fetch notifications when the component mounts
  useEffect(() => {
    console.log('Fetching notifications for agent dashboard');
    fetchNotifications();
  }, []);

  if (!userProfile || !['agent', 'superagent'].includes(userProfile.role)) {
    return <Alert severity="error">Access denied. Agent access only.</Alert>;
  }

  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: 'background.default',
      minHeight: '100vh',
      py: { xs: 2, sm: 4 },
      overflowX: 'hidden',
    }}>
      {/* Padding must be >= the Grid spacing below, otherwise the Grid's negative
          margins push content past the viewport (right edge gets clipped on phones). */}
      <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant={isMobile ? "h4" : "h3"}
                sx={{ 
                  fontWeight: 800,
                  mb: 1,
                  background: 'linear-gradient(45deg, #1976d2, #64b5f6)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  fontSize: isMobile ? '1.75rem' : '2.5rem',
                  lineHeight: isMobile ? 1.2 : 1.3,
                }}
              >
                My Collection Dashboard
              </Typography>
            </Box>
          </Grid>

          {/* Stories */}
          <Grid item xs={12} sx={{ mb: { xs: 1, sm: 2 } }}>
            <StoriesBar />
          </Grid>

          {/* Agent Notifications - Shown just below the page title */}
          <Grid item xs={12}>
            <NotificationsPanel
              notifications={notifications.filter(n =>
                ['assignment', 'collection_case', 'itinerary_submitted', 'comment_mention', 'update_mention', 'reminder'].includes(n.notification_type)
              )}
              onDismissNotification={handleDismissNotification}
              onClickNotification={handleNotificationWithMentions}
              onMarkAllAsRead={markAllAsRead}
              onRefresh={fetchNotifications}
              onClearAll={handleClearAllNotifications}
              title="Agent Notifications"
              maxHeight={isMobile ? 600 : 500}
              loading={loadingNotifications}
              defaultTab="unread"
            />
          </Grid>

          {/* Total Collections Card - Moved below title */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                mb: 2
              }}
            >
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalance sx={{ color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      ₱{formatCurrency(totalMonthlyCollection)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      My Monthly Collections
                    </Typography>
                  </Box>
                </Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center'
                  }}
                >
                  {format(new Date(), 'MMMM yyyy')}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* ── Performance Insights ─────────────────────────────────────── */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Performance Insights
              </Typography>
              <IconButton
                size="small"
                onClick={fetchInsightsAndRankings}
                disabled={insightsLoading}
                sx={{ color: 'text.secondary' }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Box>

            {/* Use Box flex instead of Grid container to avoid negative-margin overlap on mobile */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {/* Today's Collection card */}
              <Paper
                  elevation={0}
                  sx={{
                    flex: { xs: '0 0 100%', sm: '1 1 calc(50% - 6px)', md: '1 1 calc(33.33% - 8px)' },
                    minWidth: 0,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: `${theme.palette.primary.main}20`, display: 'flex', alignItems: 'center' }}>
                      <CalendarToday sx={{ color: 'primary.main', fontSize: 16 }} />
                    </Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Today's Collection
                    </Typography>
                  </Box>

                  {insightsLoading ? (
                    <>
                      <Skeleton variant="text" width="60%" height={36} />
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="70%" />
                    </>
                  ) : (
                    <>
                      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1.25 }}>
                        ₱{formatCurrency(todayCollection)}
                      </Typography>

                      {/* vs last week same day */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 18, height: 18, borderRadius: '50%',
                          bgcolor: todayCollection >= lastWeekSameDayCollection
                            ? `${theme.palette.success.main}20`
                            : `${theme.palette.error.main}20`,
                        }}>
                          {todayCollection >= lastWeekSameDayCollection
                            ? <ArrowUpward sx={{ fontSize: 11, color: 'success.main' }} />
                            : <ArrowDownward sx={{ fontSize: 11, color: 'error.main' }} />}
                        </Box>
                        <Typography
                          variant="caption"
                          color={todayCollection >= lastWeekSameDayCollection ? 'success.main' : 'error.main'}
                          fontWeight={600}
                        >
                          ₱{formatCurrency(Math.abs(todayCollection - lastWeekSameDayCollection))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          vs same day last week
                        </Typography>
                      </Box>

                      {/* vs last month same day */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 18, height: 18, borderRadius: '50%',
                          bgcolor: todayCollection >= lastMonthSameDayCollection
                            ? `${theme.palette.success.main}20`
                            : `${theme.palette.error.main}20`,
                        }}>
                          {todayCollection >= lastMonthSameDayCollection
                            ? <ArrowUpward sx={{ fontSize: 11, color: 'success.main' }} />
                            : <ArrowDownward sx={{ fontSize: 11, color: 'error.main' }} />}
                        </Box>
                        <Typography
                          variant="caption"
                          color={todayCollection >= lastMonthSameDayCollection ? 'success.main' : 'error.main'}
                          fontWeight={600}
                        >
                          ₱{formatCurrency(Math.abs(todayCollection - lastMonthSameDayCollection))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          vs same day last month
                        </Typography>
                      </Box>
                    </>
                  )}
                </Paper>

              {/* Weekly Collection card */}
              <Paper
                  elevation={0}
                  sx={{
                    flex: { xs: '0 0 100%', sm: '1 1 calc(50% - 6px)', md: '1 1 calc(33.33% - 8px)' },
                    minWidth: 0,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.info.main}10 0%, ${theme.palette.info.main}05 100%)`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: `${theme.palette.info.main}20`, display: 'flex', alignItems: 'center' }}>
                      <DateRange sx={{ color: 'info.main', fontSize: 16 }} />
                    </Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      This Week
                    </Typography>
                  </Box>

                  {insightsLoading ? (
                    <>
                      <Skeleton variant="text" width="60%" height={36} />
                      <Skeleton variant="text" width="80%" />
                    </>
                  ) : (
                    <>
                      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1.25 }}>
                        ₱{formatCurrency(weeklyCollection)}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 18, height: 18, borderRadius: '50%',
                          bgcolor: weeklyCollection >= lastWeekCollection
                            ? `${theme.palette.success.main}20`
                            : `${theme.palette.error.main}20`,
                        }}>
                          {weeklyCollection >= lastWeekCollection
                            ? <ArrowUpward sx={{ fontSize: 11, color: 'success.main' }} />
                            : <ArrowDownward sx={{ fontSize: 11, color: 'error.main' }} />}
                        </Box>
                        <Typography
                          variant="caption"
                          color={weeklyCollection >= lastWeekCollection ? 'success.main' : 'error.main'}
                          fontWeight={600}
                        >
                          ₱{formatCurrency(Math.abs(weeklyCollection - lastWeekCollection))}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          vs last week total
                        </Typography>
                      </Box>

                      {weeklyRank && (
                        <Typography variant="caption" color="text.secondary">
                          Ranked{' '}
                          <Typography component="span" variant="caption" fontWeight={700} color="info.main">
                            #{weeklyRank.rank}
                          </Typography>
                          {' '}this week among {weeklyRank.total} agents
                        </Typography>
                      )}
                    </>
                  )}
                </Paper>

              {/* Rankings card */}
              <Paper
                  elevation={0}
                  sx={{
                    flex: { xs: '0 0 100%', sm: '0 0 100%', md: '1 1 calc(33.33% - 8px)' },
                    minWidth: 0,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.warning.main}10 0%, ${theme.palette.warning.main}05 100%)`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: `${theme.palette.warning.main}20`, display: 'flex', alignItems: 'center' }}>
                      <EmojiEvents sx={{ color: 'warning.main', fontSize: 16 }} />
                    </Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Your Rankings
                    </Typography>
                  </Box>

                  {insightsLoading ? (
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      {[0,1,2].map(i => (
                        <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                          <Skeleton variant="circular" width={48} height={48} sx={{ mx: 'auto', mb: 0.5 }} />
                          <Skeleton variant="text" width="70%" sx={{ mx: 'auto' }} />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-around' }}>
                      {[
                        { label: 'Today', data: dailyRank, color: theme.palette.warning.main },
                        { label: 'This Week', data: weeklyRank, color: theme.palette.info.main },
                        { label: 'This Month', data: monthlyRank, color: theme.palette.success.main },
                      ].map(({ label, data, color }) => (
                        <Box key={label} sx={{ textAlign: 'center', flex: 1 }}>
                          <Box sx={{
                            width: 48, height: 48, borderRadius: '50%',
                            bgcolor: `${color}20`,
                            border: `2px solid ${color}40`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            mx: 'auto', mb: 0.75,
                          }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color }}>
                              {data ? `#${data.rank}` : '—'}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', lineHeight: 1.2 }}>
                            {label}
                          </Typography>
                          {data && (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                              of {data.total}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
            </Box>
          </Grid>

          {/* ── Team Momentum ──────────────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: isMobile ? 1.25 : 1.75,
                border: '1px solid',
                borderColor: teamInsights.teamMonthChangePct >= 0 ? '#4caf5040' : '#ff980040',
                borderRadius: 2,
                background: teamInsights.teamMonthChangePct >= 0
                  ? 'linear-gradient(135deg, #4caf5012 0%, #4caf5005 100%)'
                  : 'linear-gradient(135deg, #ff980012 0%, #ff980005 100%)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <TrendingUp sx={{ fontSize: isMobile ? '1rem' : '1.2rem', color: teamInsights.teamMonthChangePct >= 0 ? '#4caf50' : '#ff9800' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Team Momentum
                  </Typography>
                </Box>
                <IconButton size="small" onClick={fetchTeamData} sx={{ p: 0.25 }}>
                  <Refresh sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                </IconButton>
              </Box>

              {teamLoading ? (
                <Box sx={{ mb: 1 }}>
                  <Skeleton variant="text" width="90%" height={16} />
                  <Skeleton variant="text" width="75%" height={16} />
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.8rem', mb: 1, lineHeight: 1.5 }}>
                  {teamInsights.teamMonthChangePct >= 10
                    ? `The team is on fire! ₱${formatCurrency(teamInsights.teamThisMonth)} collected this month — ${teamInsights.teamMonthChangePct.toFixed(1)}% ahead of last month. Keep the momentum!`
                    : teamInsights.teamMonthChangePct >= 0
                    ? `Good progress! ₱${formatCurrency(teamInsights.teamThisMonth)} collected this month, up ${teamInsights.teamMonthChangePct.toFixed(1)}% from last month (₱${formatCurrency(teamInsights.teamLastMonth)}). Push harder!`
                    : teamInsights.teamThisMonth === 0
                    ? `No collections recorded yet this month. Let's get the team moving — every peso counts!`
                    : `The team has collected ₱${formatCurrency(teamInsights.teamThisMonth)} this month. We need ₱${formatCurrency(teamInsights.teamLastMonth - teamInsights.teamThisMonth)} more to match last month (₱${formatCurrency(teamInsights.teamLastMonth)}). Push it!`
                  }
                </Typography>
              )}

              {teamInsights.teamLastMonth > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.65rem' }}>
                      vs {format(subMonths(new Date(), 1), 'MMMM')} (₱{formatCurrency(teamInsights.teamLastMonth)})
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: isMobile ? '0.6rem' : '0.65rem', color: teamInsights.teamMonthChangePct >= 0 ? 'success.main' : 'error.main' }}>
                      {Math.min(Math.round((teamInsights.teamThisMonth / teamInsights.teamLastMonth) * 100), 999)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((teamInsights.teamThisMonth / teamInsights.teamLastMonth) * 100, 100)}
                    sx={{
                      height: 6, borderRadius: 3,
                      bgcolor: `${teamInsights.teamMonthChangePct >= 0 ? '#4caf50' : '#ff9800'}25`,
                      '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: teamInsights.teamMonthChangePct >= 0 ? '#4caf50' : '#ff9800' }
                    }}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: isMobile ? 0.75 : 1, mt: 1.25, flexWrap: 'wrap' }}>
                {[
                  { label: 'This Week', value: teamInsights.teamThisWeek, pct: teamInsights.teamWeekChangePct },
                  { label: 'Last Week', value: teamInsights.teamLastWeek, pct: null },
                  { label: 'Active Today', value: Object.keys(todayByAgent).length, pct: null, isCount: true },
                  { label: 'Today Total', value: Object.values(todayByAgent).reduce((s, v) => s + v, 0), pct: null },
                ].map(stat => (
                  <Box key={stat.label} sx={{ flex: '1 1 0', minWidth: isMobile ? 70 : 90, p: isMobile ? 0.6 : 0.75, bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.55rem' : '0.6rem', display: 'block', mb: 0.2 }}>
                      {stat.label}
                    </Typography>
                    {teamLoading ? (
                      <Skeleton variant="text" width="80%" height={isMobile ? 18 : 20} />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: isMobile ? '0.75rem' : '0.8rem', lineHeight: 1.2 }}>
                        {stat.isCount ? stat.value : `₱${formatCurrency(stat.value as number)}`}
                      </Typography>
                    )}
                    {stat.pct !== null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2 }}>
                        {(stat.pct as number) >= 0 ? <ArrowUpward sx={{ fontSize: '0.55rem', color: 'success.main' }} /> : <ArrowDownward sx={{ fontSize: '0.55rem', color: 'error.main' }} />}
                        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: (stat.pct as number) >= 0 ? 'success.main' : 'error.main' }}>
                          {Math.abs(stat.pct as number).toFixed(0)}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {teamInsights.topPerformer && (
                  <Chip
                    icon={<EmojiEvents sx={{ fontSize: '0.7rem !important' }} />}
                    label={`${teamInsights.topPerformer} leads — ₱${formatCurrency(teamSummaries[0]?.total_collections || 0)}`}
                    size="small"
                    sx={{ height: 20, fontSize: isMobile ? '0.6rem' : '0.65rem', bgcolor: '#ff980015', color: '#e65100', '& .MuiChip-label': { px: 0.75 } }}
                  />
                )}
                {teamInsights.needsAttention.length > 0 && (
                  <Chip
                    label={`Follow up: ${teamInsights.needsAttention.join(', ')}`}
                    size="small"
                    color="warning"
                    sx={{ height: 20, fontSize: isMobile ? '0.6rem' : '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                  />
                )}
                <Chip
                  label={`${teamSummaries.filter(c => c.this_week > 0).length}/${teamSummaries.length} active this week`}
                  size="small"
                  color="info"
                  sx={{ height: 20, fontSize: isMobile ? '0.6rem' : '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* ── Monthly Leaderboard ──────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{
                p: isMobile ? 1 : 1.5,
                borderBottom: '1px solid', borderColor: 'divider',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, transparent 100%)`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <EmojiEvents sx={{ fontSize: isMobile ? '1.1rem' : '1.3rem', color: '#ff9800' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: isMobile ? '0.875rem' : '1rem', lineHeight: 1.2 }}>
                      Monthly Leaderboard
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.65rem' }}>
                      {format(new Date(), 'MMMM yyyy')} · Tap any agent for details
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={`${teamSummaries.length} agents`}
                  size="small" color="primary" variant="outlined"
                  sx={{ height: 20, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.75 } }}
                />
              </Box>

              {/* Podium skeleton */}
              {teamLoading && (
                <Box
                  sx={{
                    px: isMobile ? 0 : 2,
                    pt: isMobile ? 1 : 1.5,
                    pb: 0.5,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    gap: isMobile ? 0.5 : 1.5,
                  }}
                >
                  {[28, 38, 28].map((w, i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: isMobile ? '1 1 0' : `0 0 ${w}%`,
                        minWidth: 0,
                        textAlign: 'center',
                      }}
                    >
                      <Skeleton variant="circular" width={isMobile ? 40 : 52} height={isMobile ? 40 : 52} sx={{ mx: 'auto', mb: 0.5 }} />
                      <Skeleton variant="text" width="70%" sx={{ mx: 'auto' }} />
                      <Skeleton variant="rounded" width="100%" height={i === 1 ? (isMobile ? 64 : 80) : (isMobile ? 44 : 56)} />
                    </Box>
                  ))}
                </Box>
              )}

              {/* Podium top 3 */}
              {!teamLoading && teamSummaries.length >= 3 && (
                <Box
                  sx={{
                    px: isMobile ? 0 : 2,
                    pt: isMobile ? 1 : 1.5,
                    pb: 0.5,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    gap: isMobile ? 0.5 : 1.5,
                  }}
                >
                  {[1, 0, 2].map((podiumIdx) => {
                    const s = teamSummaries[podiumIdx];
                    const podiumRank = podiumIdx === 0 ? 1 : podiumIdx === 1 ? 2 : 3;
                    const heights = { 1: isMobile ? 64 : 80, 2: isMobile ? 48 : 60, 3: isMobile ? 40 : 52 };
                    const colors = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };
                    const bgColors = { 1: '#FFF8E1', 2: '#F5F5F5', 3: '#FFF3E0' };
                    const todayAmt = todayByAgent[s.collector_id] || 0;
                    const isMe = s.collector_id === user?.id;
                    return (
                      <Box
                        key={s.collector_id}
                        onClick={() => setExpandedAgent(expandedAgent === s.collector_id ? null : s.collector_id)}
                        sx={{
                          flex: isMobile ? '1 1 0' : (podiumIdx === 0 ? '0 0 38%' : '0 0 28%'),
                          minWidth: 0,
                          textAlign: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <Box sx={{ position: 'relative', display: 'inline-block', mb: 0.5 }}>
                          <Avatar
                            src={s.avatar_url || undefined}
                            sx={{
                              width: podiumIdx === 0 ? (isMobile ? 44 : 52) : (isMobile ? 36 : 44),
                              height: podiumIdx === 0 ? (isMobile ? 44 : 52) : (isMobile ? 36 : 44),
                              bgcolor: isMe ? theme.palette.primary.main : colors[podiumRank as keyof typeof colors],
                              color: isMe ? 'white' : '#333',
                              fontWeight: 800,
                              fontSize: podiumIdx === 0 ? (isMobile ? '1rem' : '1.2rem') : (isMobile ? '0.8rem' : '1rem'),
                              border: `3px solid ${isMe ? theme.palette.primary.main : colors[podiumRank as keyof typeof colors]}`,
                              boxShadow: isMe ? `0 0 0 3px ${theme.palette.primary.main}40` : podiumIdx === 0 ? `0 0 0 3px ${colors[1]}40` : 'none',
                              mx: 'auto',
                            }}
                          >
                            {!s.avatar_url && s.collector_name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{
                            position: 'absolute', bottom: -4, right: -4,
                            width: isMobile ? 16 : 18, height: isMobile ? 16 : 18, borderRadius: '50%',
                            bgcolor: colors[podiumRank as keyof typeof colors],
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '1px solid white',
                          }}>
                            <Typography sx={{ fontWeight: 900, fontSize: isMobile ? '0.5rem' : '0.55rem', color: '#333' }}>
                              {podiumRank}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography sx={{
                          fontWeight: isMe ? 800 : podiumIdx === 0 ? 800 : 600,
                          fontSize: isMobile ? '0.65rem' : '0.72rem',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          color: isMe ? 'primary.main' : 'inherit',
                        }}>
                          {s.collector_name}{isMe ? ' (You)' : ''}
                        </Typography>
                        <Box sx={{
                          height: heights[podiumRank as keyof typeof heights],
                          bgcolor: bgColors[podiumRank as keyof typeof bgColors],
                          border: `2px solid ${isMe ? theme.palette.primary.main : colors[podiumRank as keyof typeof colors]}`,
                          borderRadius: '4px 4px 0 0', mt: 0.5,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 0.5,
                        }}>
                          <Typography sx={{ fontWeight: 800, fontSize: isMobile ? '0.72rem' : '0.8rem', color: '#333', lineHeight: 1.2 }}>
                            ₱{formatCurrency(s.total_collections)}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: isMobile ? '0.5rem' : '0.55rem', color: 'text.secondary', lineHeight: 1.2 }}>
                            this month
                          </Typography>
                          {todayAmt > 0 && (
                            <Chip label={`+₱${formatCurrency(todayAmt)}`} size="small"
                              sx={{ mt: 0.3, height: 14, fontSize: '0.48rem', bgcolor: '#4caf5020', color: '#2e7d32', '& .MuiChip-label': { px: 0.5 } }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Full ranked list */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {teamSummaries.map((summary, index) => {
                  const topAmount = teamSummaries[0]?.total_collections || 1;
                  const progressPct = (summary.total_collections / topAmount) * 100;
                  const gapToNext = index > 0 ? (teamSummaries[index - 1].total_collections - summary.total_collections) : 0;
                  const todayAmt = todayByAgent[summary.collector_id] || 0;
                  const medalColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
                  const isMe = summary.collector_id === user?.id;

                  return (
                    <Box key={summary.collector_id}>
                      <Box
                        onClick={() => setExpandedAgent(expandedAgent === summary.collector_id ? null : summary.collector_id)}
                        sx={{
                          display: 'flex', alignItems: 'center',
                          py: isMobile ? 0.75 : 1, px: isMobile ? 1 : 1.5,
                          cursor: 'pointer',
                          bgcolor: isMe ? `${theme.palette.primary.main}08` : 'transparent',
                          transition: 'background 0.15s',
                          '&:hover': { bgcolor: 'action.hover' },
                          ...(index !== teamSummaries.length - 1 && expandedAgent !== summary.collector_id && { borderBottom: '1px solid', borderColor: 'divider' }),
                        }}
                      >
                        <Box sx={{ width: isMobile ? 28 : 36, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Typography variant="caption" sx={{
                            fontWeight: 800, fontSize: isMobile ? '0.7rem' : '0.75rem',
                            color: index < 3 ? medalColors[index] : 'text.disabled', minWidth: 14,
                          }}>
                            {index + 1}
                          </Typography>
                          {index === 0 && <EmojiEvents sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#FFD700' }} />}
                          {index === 1 && <EmojiEvents sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#C0C0C0' }} />}
                          {index === 2 && <EmojiEvents sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#CD7F32' }} />}
                          {index >= 3 && <Person sx={{ fontSize: isMobile ? '0.75rem' : '0.85rem', color: 'text.disabled' }} />}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, mx: isMobile ? 0.75 : 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                            <Typography variant="body2" sx={{
                              fontWeight: isMe ? 800 : index < 3 ? 700 : 500,
                              fontSize: isMobile ? '0.78rem' : '0.83rem',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                              color: isMe ? 'primary.main' : 'inherit',
                            }}>
                              {summary.collector_name}{isMe ? ' (You)' : ''}
                            </Typography>
                            {todayAmt > 0 && (
                              <Chip label={`+₱${formatCurrency(todayAmt)}`} size="small"
                                sx={{ height: 16, fontSize: '0.55rem', bgcolor: '#4caf5018', color: '#2e7d32', flexShrink: 0, '& .MuiChip-label': { px: 0.6 } }}
                              />
                            )}
                          </Box>
                          <LinearProgress
                            variant="determinate" value={progressPct}
                            sx={{
                              height: 4, borderRadius: 2,
                              bgcolor: `${isMe ? theme.palette.primary.main : index < 3 ? medalColors[index] : theme.palette.primary.main}20`,
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 2,
                                bgcolor: isMe ? theme.palette.primary.main : index < 3 ? medalColors[index] : theme.palette.primary.main,
                              }
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15 }}>
                              {summary.week_change_pct >= 0
                                ? <ArrowUpward sx={{ fontSize: '0.5rem', color: 'success.main' }} />
                                : <ArrowDownward sx={{ fontSize: '0.5rem', color: 'error.main' }} />}
                              <Typography variant="caption" sx={{ fontSize: isMobile ? '0.52rem' : '0.57rem', color: summary.week_change_pct >= 0 ? 'success.main' : 'error.main' }}>
                                {summary.week_change_pct >= 0 ? '+' : ''}{summary.week_change_pct.toFixed(0)}% wk
                              </Typography>
                            </Box>
                            {gapToNext > 0 && (
                              <Typography variant="caption" sx={{ fontSize: isMobile ? '0.52rem' : '0.57rem', color: 'text.disabled' }}>
                                · ₱{formatCurrency(gapToNext)} to #{index}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Typography variant="body2" sx={{
                          fontWeight: 700,
                          color: summary.total_collections > 0 ? (isMe ? 'primary.main' : 'success.main') : 'text.disabled',
                          fontSize: isMobile ? '0.78rem' : '0.83rem',
                          textAlign: 'right', flexShrink: 0,
                        }}>
                          ₱{formatCurrency(summary.total_collections)}
                        </Typography>
                      </Box>

                      {expandedAgent === summary.collector_id && (
                        <Box sx={{ px: isMobile ? 1 : 1.5, py: isMobile ? 0.75 : 1, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Grid container spacing={isMobile ? 0.5 : 1}>
                            {[
                              { label: 'This Week', value: summary.this_week, pct: summary.week_change_pct, pctLabel: 'vs last week' },
                              { label: 'Last Week', value: summary.last_week, pct: null, pctLabel: '' },
                              { label: 'This Month', value: summary.this_month, pct: summary.month_change_pct, pctLabel: 'vs last month' },
                              { label: 'Last Month', value: summary.last_month, pct: null, pctLabel: '' },
                            ].map(stat => (
                              <Grid item xs={6} key={stat.label}>
                                <Box sx={{ p: 0.75, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>{stat.label}</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: isMobile ? '0.78rem' : '0.83rem' }}>
                                    ₱{formatCurrency(stat.value)}
                                  </Typography>
                                  {stat.pct !== null && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2 }}>
                                      {(stat.pct as number) >= 0 ? <ArrowUpward sx={{ fontSize: '0.5rem', color: 'success.main' }} /> : <ArrowDownward sx={{ fontSize: '0.5rem', color: 'error.main' }} />}
                                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: (stat.pct as number) >= 0 ? 'success.main' : 'error.main' }}>
                                        {Math.abs(stat.pct as number).toFixed(1)}% {stat.pctLabel}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                          <Box sx={{
                            mt: 0.75, p: 0.75, borderRadius: 1,
                            bgcolor: isMe
                              ? `${theme.palette.primary.main}10`
                              : summary.week_change_pct >= 10 ? '#4caf5012' : summary.week_change_pct >= 0 ? '#2196f312' : summary.this_week === 0 ? '#f4433612' : '#ff980012',
                            borderLeft: `3px solid ${isMe ? theme.palette.primary.main : summary.week_change_pct >= 10 ? '#4caf50' : summary.week_change_pct >= 0 ? '#2196f3' : summary.this_week === 0 ? '#f44336' : '#ff9800'}`,
                          }}>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.5 }}>
                              {isMe
                                ? summary.week_change_pct >= 10
                                  ? `🔥 You're on fire — up ${summary.week_change_pct.toFixed(0)}% this week! Rank #${index + 1} of ${teamSummaries.length}. Keep it up!`
                                  : summary.week_change_pct >= 0 && summary.this_week > 0
                                  ? `👍 You're trending up. Rank #${index + 1}. ${gapToNext > 0 ? `₱${formatCurrency(gapToNext)} away from rank #${index}.` : 'You lead the board!'}`
                                  : summary.this_week === 0
                                  ? `📋 No collections this week yet. Every visit counts — get moving!`
                                  : `📉 You're down ${Math.abs(summary.week_change_pct).toFixed(0)}% this week vs last. Push for more collections today!`
                                : summary.week_change_pct >= 20
                                ? `🔥 ${summary.collector_name} is on fire — up ${summary.week_change_pct.toFixed(0)}% this week!`
                                : summary.week_change_pct >= 0 && summary.this_week > 0
                                ? `👍 ${summary.collector_name} is trending up. Rank #${index + 1}.`
                                : summary.this_week === 0 && summary.last_week === 0
                                ? `⚠️ ${summary.collector_name} has no recent collections.`
                                : summary.this_week === 0
                                ? `🚨 ${summary.collector_name} had ₱${formatCurrency(summary.last_week)} last week but nothing this week.`
                                : `📉 ${summary.collector_name} is down ${Math.abs(summary.week_change_pct).toFixed(0)}% this week.`
                              }
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Grid>

          {/* ── Tip of the Day ──────────────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.75, sm: 2 },
                border: '1px solid',
                borderColor: `${theme.palette.warning.main}40`,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.warning.main}08 0%, ${theme.palette.warning.light}05 100%)`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.25, sm: 1.75 } }}>
                {/* Icon */}
                <Box sx={{
                  p: { xs: 0.75, sm: 1 },
                  borderRadius: 2,
                  bgcolor: theme.palette.warning.main,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, mt: 0.25,
                }}>
                  <Lightbulb sx={{ color: '#fff', fontSize: { xs: 16, sm: 20 } }} />
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="warning.dark">
                      Tip of the Day
                    </Typography>
                    <Chip
                      label={currentTip.category}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        bgcolor: `${theme.palette.warning.main}20`,
                        color: 'warning.dark',
                        textTransform: 'capitalize',
                        letterSpacing: 0.3,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ lineHeight: 1.65, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {currentTip.text}
                  </Typography>
                </Box>

                {/* Refresh */}
                <IconButton
                  size="small"
                  onClick={() => setCurrentTip(getContextualTip())}
                  sx={{ color: 'warning.main', flexShrink: 0, alignSelf: 'flex-start' }}
                  title="Get a new tip"
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </Grid>

          
          {/* Monthly Collections Chart - Hidden on mobile */}
          {!isMobile && (
            <Grid item xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  mb: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                  Monthly Collections Trend
                </Typography>
                <Box sx={{ height: 300 }}>
                  <Bar
                    data={{
                      labels: monthlyCollections.map(item => item.month),
                      datasets: [
                        {
                          label: 'Total Collections',
                          data: monthlyCollections.map(item => item.amount),
                          backgroundColor: `${theme.palette.primary.main}80`,
                          borderColor: theme.palette.primary.main,
                          borderWidth: 1,
                          borderRadius: 6,
                          maxBarThickness: 40,
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        title: {
                          display: false,
                        },
                        tooltip: {
                          backgroundColor: theme.palette.background.paper,
                          titleColor: theme.palette.text.primary,
                          bodyColor: theme.palette.text.secondary,
                          borderColor: theme.palette.divider,
                          borderWidth: 1,
                          padding: 12,
                          cornerRadius: 8,
                          callbacks: {
                            label: (context) => `₱${formatCurrency(context.raw as number)}`
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: theme.palette.divider,
                          },
                          border: {
                            display: false
                          },
                          ticks: {
                            callback: (value) => `₱${formatCurrency(value as number)}`,
                            font: {
                              size: 11
                            }
                          }
                        },
                        x: {
                          grid: {
                            display: false
                          },
                          border: {
                            display: false
                          },
                          ticks: {
                            font: {
                              size: 11
                            }
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          )}
          
          {/* Cases Section */}
          <Grid item xs={12}>
            {/* For Collection Cases */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              For Collection Cases
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : filterCasesByType(unassignedCases, 'collection').length === 0 ? (
              <Alert 
                severity="info"
                sx={{
                  borderRadius: 2
                }}
              >
                No unassigned collection cases
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Borrower Name</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      {!isMobile && (
                        <>
                          <StyledTableCell>Mobile No.</StyledTableCell>
                          <StyledTableCell>Email</StyledTableCell>
                          <StyledTableCell>Address</StyledTableCell>
                          <StyledTableCell>Agent</StyledTableCell>
                          <StyledTableCell>Loandisk ID</StyledTableCell>
                          <StyledTableCell align="center">Actions</StyledTableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterCasesByType(unassignedCases, 'collection').map((caseItem) => (
                      <TableRow 
                        key={caseItem.id}
                        sx={{
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <TableCell>{caseItem.borrower?.name || 'N/A'}</TableCell>
                        <TableCell>
                          <StatusChip status={caseItem.status} />
                        </TableCell>
                        {!isMobile && (
                          <>
                            <TableCell>{caseItem.borrower?.mobile_no || 'N/A'}</TableCell>
                            <TableCell>{caseItem.borrower?.email || 'N/A'}</TableCell>
                            <TableCell>{caseItem.borrower?.address || 'N/A'}</TableCell>
                            <TableCell>{caseItem.agent_id ? 'Assigned' : 'Unassigned'}</TableCell>
                            <TableCell>{caseItem.loandisk_id || 'N/A'}</TableCell>
                            <TableCell align="center">
                              <AssignButton onClick={() => setSelectedCase(caseItem)} />
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* For CI Cases */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, mt: 4 }}>
              For CI Cases
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : filterCasesByType(unassignedCases, 'ci').length === 0 ? (
              <Alert 
                severity="info"
                sx={{
                  borderRadius: 2
                }}
              >
                No unassigned CI cases
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Borrower Name</StyledTableCell>
                      <StyledTableCell>Status</StyledTableCell>
                      {!isMobile && (
                        <>
                          <StyledTableCell>Mobile No.</StyledTableCell>
                          <StyledTableCell>Email</StyledTableCell>
                          <StyledTableCell>Address</StyledTableCell>
                          <StyledTableCell>Agent</StyledTableCell>
                          <StyledTableCell>Loandisk ID</StyledTableCell>
                          <StyledTableCell align="center">Actions</StyledTableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterCasesByType(unassignedCases, 'ci').map((caseItem) => (
                      <TableRow 
                        key={caseItem.id}
                        sx={{
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <TableCell>{caseItem.borrower?.name || 'N/A'}</TableCell>
                        <TableCell>
                          <StatusChip status={caseItem.status} />
                        </TableCell>
                        {!isMobile && (
                          <>
                            <TableCell>{caseItem.borrower?.mobile_no || 'N/A'}</TableCell>
                            <TableCell>{caseItem.borrower?.email || 'N/A'}</TableCell>
                            <TableCell>{caseItem.borrower?.address || 'N/A'}</TableCell>
                            <TableCell>{caseItem.agent_id || 'Unassigned'}</TableCell>
                            <TableCell>{caseItem.loandisk_id || 'N/A'}</TableCell>
                            <TableCell align="center">
                              <AssignButton onClick={() => setSelectedCase(caseItem)} />
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AgentDashboard;
